import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  defaultIfEmpty,
  delay,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs';

import {
  CalendarCellClickEvent,
  CalendarRoomRow,
  WeekCalendarComponent,
} from '@bookly/shared';

import { FiltersFormService } from '../../components/room-filters-form/filters-form.service';
import { RoomsService } from '../../services/rooms.service';
import { UserCalendarService } from '../../services/user-calendar.service';

@Component({
  selector: 'bus-calendar-overview',
  imports: [WeekCalendarComponent],
  templateUrl: './calendar-overview.component.html',
  styleUrl: './calendar-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarOverviewComponent implements OnInit, OnDestroy {
  readonly #userCalendarService = inject(UserCalendarService);
  readonly #roomsService = inject(RoomsService);
  readonly #filtersFormService = inject(FiltersFormService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);

  protected readonly calendarRows = this.#userCalendarService.calendarRows;
  protected readonly isLoading = this.#userCalendarService.isLoading;
  protected readonly sortDirection = signal<'asc' | 'desc'>('asc');
  protected readonly initialDate = toSignal(
    this.#filtersFormService.filtersForm.controls.fromDate.valueChanges.pipe(
      startWith(this.#filtersFormService.filtersForm.controls.fromDate.value),
      map(date => date ?? new Date())
    ),
    { initialValue: new Date() }
  );
  readonly #currentRange = new BehaviorSubject<{
    startDate: Date;
    endDate: Date;
  } | null>(null);

  ngOnInit() {
    this.#roomsService
      .getRoomFilters()
      .pipe(
        defaultIfEmpty(null),
        delay(50),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();

    // Sync the form into the URL query params (the single source of truth that
    // also carries `favorite` and the calendar's `fromDate`) and refresh the
    // active-filter chips. The reload is NOT triggered from here: the router
    // navigation is asynchronous, so reading the URL right after would see the
    // stale params. Instead we react to the resulting query-param change below.
    this.#filtersFormService.filtersForm.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#filtersFormService.getSelectedFormData());

    // Reload whenever the visible week range or the URL query params change.
    // The debounce collapses the range + query-param updates that a single week
    // navigation emits together into one request.
    combineLatest([this.#currentRange, this.#route.queryParams])
      .pipe(
        filter(([range]) => range !== null),
        debounceTime(50),
        switchMap(([range]) =>
          this.#userCalendarService.loadBookings(
            range!.startDate,
            range!.endDate
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.#userCalendarService.clear();
  }

  protected onWeekChange(range: {
    startDate: Date;
    endDate: Date;
    selectedDate: Date;
    userInitiated: boolean;
  }) {
    // The data range is the calendar's visible week of the selected day (which
    // defaults to fromDate, or today when it is not set). With weekends hidden
    // the visible range spills into the next week (Mon–Tue), so the emitted
    // start/end are used verbatim to cover exactly what the calendar shows.
    this.#currentRange.next({
      startDate: range.startDate,
      endDate: range.endDate,
    });
    if (range.userInitiated) {
      const fromDateControl =
        this.#filtersFormService.filtersForm.controls.fromDate;
      const currentFromDate = fromDateControl.value;
      if (
        !currentFromDate ||
        currentFromDate.getTime() !== range.selectedDate.getTime()
      ) {
        fromDateControl.setValue(range.selectedDate, { emitEvent: false });
      }
    }
    // Push the (possibly updated) selection into the URL; the reload is driven
    // by the resulting range/query-param change (see ngOnInit).
    this.#filtersFormService.getSelectedFormData();
  }

  protected onRoomClick(room: CalendarRoomRow) {
    void this.#router.navigate(['/dashboard', 'rooms', room.roomDexId]);
  }

  protected onAddEvent(cell: CalendarCellClickEvent) {
    void this.#router.navigate([
      'dashboard',
      'rooms',
      cell.roomDexId,
      'create',
      { eventDate: cell.date },
    ]);
  }

  protected onSortChange(sort: 'asc' | 'desc') {
    this.sortDirection.set(sort);
  }
}
